import * as TabsPrimitive from '@radix-ui/react-tabs';
import cn from 'classnames';
import React from 'react';

import styles from './Tabs.module.scss';

type TabSize = 'normal' | 'small';

type TabVariant = 'default' | 'pill';

type TabsListProps = TabsPrimitive.TabsListProps & {
  size?: TabSize;
  variant?: TabVariant;
};

type TabsTriggerProps = TabsPrimitive.TabsTriggerProps & {
  size?: TabSize;
  variant?: TabVariant;
};

const sizeStyle: Record<TabSize, string> = {
  normal: styles.sizeNormal,
  small: styles.sizeSmall,
};

const variantStyle: Record<TabVariant, string | undefined> = {
  default: undefined,
  pill: styles.variantPill,
};

const InternalTabsRoot: React.ForwardRefRenderFunction<
  HTMLDivElement,
  TabsPrimitive.TabsProps
> = (props, ref) => {
  const { className, children, ...rest } = props;

  return (
    <TabsPrimitive.Root
      ref={ref}
      className={cn(styles.root, className)}
      {...rest}
    >
      {children}
    </TabsPrimitive.Root>
  );
};

const InternalTabsTrigger: React.ForwardRefRenderFunction<
  HTMLButtonElement,
  TabsTriggerProps
> = (props, ref) => {
  const {
    className,
    children,
    size = 'normal',
    variant = 'default',
    ...rest
  } = props;

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        styles.trigger,
        sizeStyle[size],
        variantStyle[variant],
        size === 'small' && 'p3',
        size === 'normal' && 'p1',
        className,
      )}
      {...rest}
    >
      {children}
    </TabsPrimitive.Trigger>
  );
};

const InternalTabsList: React.ForwardRefRenderFunction<
  HTMLDivElement,
  TabsListProps
> = (props, ref) => {
  const {
    className,
    children,
    size = 'normal',
    variant = 'default',
    ...rest
  } = props;

  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        styles.list,
        sizeStyle[size],
        variantStyle[variant],
        className,
      )}
      {...rest}
    >
      {children}
    </TabsPrimitive.List>
  );
};

const TabsList = React.forwardRef<HTMLDivElement, TabsListProps>(
  InternalTabsList,
);

const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(
  InternalTabsTrigger,
);

const TabsRoot = React.forwardRef<HTMLDivElement, TabsPrimitive.TabsProps>(
  InternalTabsRoot,
);

type CompoundedComponent = {
  Root: typeof TabsRoot;
  List: typeof TabsList;
  Trigger: typeof TabsTrigger;
  Content: typeof TabsPrimitive.Content;
};

export const Tabs: CompoundedComponent = {
  Root: TabsRoot,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsPrimitive.Content,
};
